import {AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild} from '@angular/core';
import {firstNestedSymbolControl, WertelisteElement, Wertemenge} from '../../../models/uiArtefakt';
import {NgForm, NgModel} from '@angular/forms';
import {BehaviorSubject, Subscription} from 'rxjs';
import {ValidationService} from '../../../services/validation.service';
import {UiUpdateService} from '../../../services/ui-update.service';
import {WertemengenService} from '../../../services/wertemengen.service';
import {ErrorStateMatcher} from '@angular/material/core';
import {ErrorStateMatcherFormInput, ISymbol, TranslationService} from '@lv1871/core';
import {UIDarstellungsart} from '../../../models/uiDarstellungsart';
import {ErrorStateMatcherFailure} from '../../../../lib-core/services/material/errorStateMatcherFailure';
import {ShellConfigService} from '../../../services/shell-config.service';
import {UIAusrichtung} from '../../../models/uiAusrichtung';
import {EventService} from '../../../services/event.service';

@Component({
    selector: 'lv-search-simple',
    templateUrl: './search-simple.component.html',
    styleUrls: ['./search-simple.component.scss'],
    providers: [WertemengenService],
    standalone: false
})
export class SearchSimpleComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild('inputmodel', {static: true})
  private _inputmodel: NgModel;

  @ViewChild(NgForm, {static: true})
  private _ngForm: NgForm;

  public model;
  public hasInputFocus: boolean;
  public stateMatcherFormInput: ErrorStateMatcher = new ErrorStateMatcherFormInput();
  public isSearching: boolean;
  public filteredOptions$: BehaviorSubject<WertelisteElement[]>;
  public isDisabled: boolean = false;
  public showUntruncatedError: boolean = false;
  public uiAusrichtung: UIAusrichtung;
  public nestedSymbol: ISymbol;
  public showInfotextCollapsable: boolean = false;

  private _uiArtefakt: Wertemenge;
  private _selectedOption: string;
  private _subscription = Subscription.EMPTY;


  constructor(private _translationService: TranslationService,
              private _validationService: ValidationService,
              private _shellConfigService: ShellConfigService,
              private _uiUpdateService: UiUpdateService,
              private _wertemengenService: WertemengenService,
              private _eventService: EventService,
              private _renderer: Renderer2, private _element: ElementRef) {
  }

  get uiArtefakt(): Wertemenge {
    return this._uiArtefakt;
  }

  @Input() set uiArtefakt(uiArtefakt: Wertemenge) {
    this._uiArtefakt = uiArtefakt;
    this.model = this.uiArtefakt.WertAnzeige;
    if (!this.model) {
      this.model = '';
    }
    this._selectedOption = this.model;
    this.isDisabled = this._uiArtefakt?.Darstellungsart === UIDarstellungsart.DISABLED;
  }

  public selectedOptionProvider: () => string = () => this._selectedOption;

  public infotextCollapsableChange(event) {
    this.showInfotextCollapsable = event;
  }

  ngOnInit(): void {
    this.uiAusrichtung = this._shellConfigService.getParameter().uiAusrichtung;
    this.nestedSymbol = firstNestedSymbolControl(this.uiArtefakt.UIArtefakt);
    this._validationService.registerForm(this.uiArtefakt, this._ngForm);
    this.filteredOptions$ = this._wertemengenService.filteredOptions$;
    this._subscription = this.filteredOptions$.subscribe(_ => {
      this.isSearching = false;
    });
    this.model = this.uiArtefakt.WertAnzeige;
    if (!this.model) {
      this.model = '';
    }
    this._selectedOption = this.model;
  }

  ngAfterViewInit(): void {
    this._renderer.setAttribute(this._element.nativeElement, 'cypress-uuid', this.uiArtefakt.IdentifikatorUuid);
    if(!!this.uiArtefakt.ReferenzIdentifikator) {
      this._renderer.setAttribute(this._element.nativeElement, 'cypress-refId', this.uiArtefakt.ReferenzIdentifikator);
    }
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  public clearInput() {
    this.model = '';
    this._selectedOption = this.model;
    this.uiArtefakt.WertEingabe = null;
    this._filter(this._selectedOption);
  }

  public setValue(value: string) {
    this._selectedOption = value;
    this._ngForm.controls[this.uiArtefakt.Identifikator].updateValueAndValidity();
    const wertEingabe = this.filteredOptions$.getValue().find(wert => wert.WertAnzeige === value)?.WertEingabe;
    if (this.uiArtefakt.WertEingabe !== wertEingabe) {
      this.uiArtefakt.WertEingabe = wertEingabe;
      this._uiUpdateService.valueChangedSubject$.next(this.uiArtefakt);
    }
  }

  public filterOptions(value: string) {
    this.model = value;
    this._filter(value);
  }

  public onBlur() {
    this.hasInputFocus = false;
    this.getError();
    if(!!this._uiArtefakt.ReferenzIdentifikator){
      this._eventService.refIdentTouchedOrDirty$.next(this._uiArtefakt.ReferenzIdentifikator);
    }
  }

  public getError(): string {
    this.stateMatcherFormInput = new ErrorStateMatcherFailure();
    if (this._inputmodel.errors?.required && this._inputmodel.touched) {
      const requiredError = this._translationService.translate('ERRORS.REQUIRED');
      return !!this._uiArtefakt.FehlertextPflichtfeld ?
          this._uiArtefakt.FehlertextPflichtfeld : requiredError;
    }
    if (this._inputmodel.errors?.ERROR && !this.hasInputFocus) {
      return this._translationService.translate(this._inputmodel.errors?.ERROR);
    }
    if (this._uiArtefakt.UIMeldung && this._uiArtefakt.UIMeldung.length && this._inputmodel.touched) {
      return this._translationService.translate(this._uiArtefakt.UIMeldung[0].Meldungstext);
    }
    this.stateMatcherFormInput = new ErrorStateMatcherFormInput();
    this.showUntruncatedError = false;
    return null;
  }

  public onErrorClick() {
    this.showUntruncatedError = !this.showUntruncatedError;
  }

  private _filter(value: string) {
    this.isSearching = this._wertemengenService.filterWertemenge(this.uiArtefakt, value);
  }
}
